import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useContactModal } from '../App';
import {
  FaStar, FaPhone, FaHandsHelping, FaGem, FaHeart,
  FaHome, FaUserAstronaut, FaQuoteLeft,
  FaArrowRight, FaOm, FaSun, FaMoon, FaVideo, FaComments,
  FaPhoneAlt, FaUsers, FaAward, FaGlobeAsia, FaCalendarAlt
} from 'react-icons/fa';
import {
  GiMeditation, GiCrystalBall, GiStarShuriken,
  GiHolyGrail, GiSpellBook
} from 'react-icons/gi';
import './Home.css';

const services = [
  { icon: <FaUserAstronaut />, title: 'Career Advice', desc: 'Expert guidance for career growth, job changes, and professional success through planetary analysis.' },
  { icon: <GiHolyGrail />, title: 'Medical Astrology', desc: 'Astrological insights for non-treatable and non-curable diseases. Healing through cosmic energy.' },
  { icon: <FaGem />, title: 'Gem Suggestion', desc: 'Precise gemstone recommendations based on your birth chart for health, wealth, and prosperity.' },
  { icon: <FaHeart />, title: 'Marriage & Progeny', desc: 'Expert guidance on marriage compatibility, timing, and blessings for children.' },
  { icon: <FaHome />, title: 'Vastu Shastra', desc: 'Scientific Vastu consultation for home, office, and commercial spaces for positive energy flow.' },
  { icon: <GiCrystalBall />, title: 'KP Astrology', desc: 'Accurate predictions using Krishnamurthy Paddhati system for precise life event timing.' },
];

const testimonials = [
  { name: 'Rajesh K.', location: 'Mumbai', rating: 5, text: 'Acharya Arun ji gave me the most accurate career guidance. Within months of following his advice, I got my dream promotion. His gem suggestion changed my life completely.' },
  { name: 'Priya S.', location: 'Delhi', rating: 5, text: 'After years of health issues, Acharya ji\'s medical astrology insights helped me find the right path to healing. His Graha Shanti remedies brought peace to our family.' },
  { name: 'Amit & Neha', location: 'Bangalore', rating: 5, text: 'Our marriage was made possible because of Acharya Arun ji\'s matchmaking. He predicted the perfect muhurta and our life together has been truly blessed.' },
  { name: 'Sunita D.', location: 'Pune', rating: 5, text: 'The Vastu corrections suggested by Acharya ji transformed our home energy completely. Business started flourishing and health issues resolved within months.' },
  { name: 'Vikram R.', location: 'Hyderabad', rating: 5, text: 'His KP astrology prediction about my foreign travel was spot on — exact month! I consult him for every major life decision now. Truly a gifted astrologer.' },
];

const zodiacData = [
  { sign: '♈', name: 'Aries', hindi: 'मेष' },
  { sign: '♉', name: 'Taurus', hindi: 'वृषभ' },
  { sign: '♊', name: 'Gemini', hindi: 'मिथुन' },
  { sign: '♋', name: 'Cancer', hindi: 'कर्क' },
  { sign: '♌', name: 'Leo', hindi: 'सिंह' },
  { sign: '♍', name: 'Virgo', hindi: 'कन्या' },
  { sign: '♎', name: 'Libra', hindi: 'तुला' },
  { sign: '♏', name: 'Scorpio', hindi: 'वृश्चिक' },
  { sign: '♐', name: 'Sagittarius', hindi: 'धनु' },
  { sign: '♑', name: 'Capricorn', hindi: 'मकर' },
  { sign: '♒', name: 'Aquarius', hindi: 'कुम्भ' },
  { sign: '♓', name: 'Pisces', hindi: 'मीन' },
];

const zodiacSigns = zodiacData.map(z => z.sign);

/* Animated counter hook */
function useCounter(target, duration = 2000) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const counted = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !counted.current) {
        counted.current = true;
        let start = 0;
        const step = target / (duration / 16);
        const timer = setInterval(() => {
          start += step;
          if (start >= target) { setCount(target); clearInterval(timer); }
          else setCount(Math.floor(start));
        }, 16);
      }
    }, { threshold: 0.5 });
    observer.observe(el);
    return () => observer.disconnect();
  }, [target, duration]);

  return [count, ref];
}

function CounterStat({ icon, target, suffix, label }) {
  const [count, ref] = useCounter(target);
  return (
    <div className="counter-card group text-center p-10 max-md:p-6 bg-white/[0.04] border border-accent-gold/10 rounded-[20px] backdrop-blur-[16px] transition-all duration-400 relative overflow-hidden hover:bg-white/[0.08] hover:border-accent-gold/30 hover:-translate-y-2 hover:shadow-[0_20px_40px_rgba(0,0,0,0.3),0_0_40px_rgba(167,139,250,0.06)]" ref={ref}>
      <div className="text-[2.2rem] text-accent-gold mb-4">{icon}</div>
      <span className="block font-heading text-[2.8rem] max-sm:text-[2rem] font-extrabold bg-gradient-to-br from-accent-yellow to-accent-saffron bg-clip-text text-transparent mb-2">{count}{suffix}</span>
      <span className="text-white/60 text-sm uppercase tracking-[1.5px]">{label}</span>
    </div>
  );
}

const Home = () => {
  const { openContactModal } = useContactModal();
  const navigate = useNavigate();

  return (
    <div>
      {/* ===== HERO SECTION ===== */}
      <section className="min-h-screen bg-gradient-hero flex flex-col items-center justify-center relative pt-[140px] pb-20 max-md:pt-[100px] max-md:pb-[50px]">
        <div className="absolute inset-0 pointer-events-none">
          <div className="cosmic-circle absolute rounded-full border border-accent-gold/[0.08] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] animate-spin-slow" style={{ boxShadow: '0 0 80px rgba(108,92,231,0.05), inset 0 0 80px rgba(108,92,231,0.03)' }} />
          <div className="cosmic-circle absolute rounded-full border top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[450px] h-[450px]" style={{ borderColor: 'rgba(108,92,231,0.1)', animation: 'spin-slow 45s linear infinite reverse', boxShadow: '0 0 60px rgba(167,139,250,0.04), inset 0 0 60px rgba(167,139,250,0.02)' }} />
          <div className="cosmic-circle absolute rounded-full border top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] h-[300px]" style={{ borderColor: 'rgba(167,139,250,0.12)', animation: 'spin-slow 30s linear infinite', boxShadow: '0 0 40px rgba(167,139,250,0.06), inset 0 0 40px rgba(167,139,250,0.03)' }} />
          {[...Array(50)].map((_, i) => (
            <div key={i} className="absolute bg-white rounded-full animate-twinkle" style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 5}s`,
              animationDuration: `${2 + Math.random() * 3}s`,
              width: `${Math.random() * 3 + 1}px`,
              height: `${Math.random() * 3 + 1}px`,
            }} />
          ))}
          <div className="zodiac-ring absolute w-[560px] h-[560px] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 max-md:hidden" style={{ animation: 'spin-slow 90s linear infinite' }}>
            {zodiacSigns.map((sign, i) => (
              <span key={i} className="absolute top-1/2 left-1/2 text-2xl text-accent-gold/15" style={{
                transform: `rotate(${i * 30}deg) translateX(280px) rotate(-${i * 30}deg)`
              }}>{sign}</span>
            ))}
          </div>
        </div>

        <div className="container-custom relative z-[2] animate-fadeInUp px-5">
          <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-10">
            {/* Hero Text */}
            <div className="text-center lg:text-left flex-1 order-2 lg:order-1">
              <div className="inline-flex items-center gap-2 py-2.5 px-7 bg-accent-gold/10 border border-accent-gold/25 rounded-full text-accent-gold text-[0.85rem] font-medium mb-6 tracking-wider backdrop-blur-[10px] animate-gradient-border">
            <GiStarShuriken /> Vedic Astrology & Cosmic Healing
          </div>
          <h1>
            <span className="block font-accent text-[1.8rem] max-md:text-[1.3rem] text-accent-saffron font-normal mb-1.5">Acharya Arun</span>
            <span className="block font-heading text-[4.2rem] max-lg:text-[3.5rem] max-md:text-[2.8rem] max-sm:text-[2rem] font-extrabold leading-[1.1] mb-2.5 bg-gradient-to-r from-accent-gold via-accent-yellow to-accent-saffron bg-[length:200%_auto] bg-clip-text text-transparent animate-shimmer drop-shadow-[0_2px_10px_rgba(249,115,22,0.15)]">Arogya Jyotish</span>
            <span className="block font-body text-[1.3rem] max-md:text-[0.9rem] text-white/80 font-light tracking-[5px] max-md:tracking-[3px] uppercase mt-1.5">Your Cosmic Guide to Life</span>
          </h1>
          <p className="max-w-[550px] lg:mx-0 mx-auto mt-6 text-[1.05rem] text-white/75 leading-[1.8]">
            Unlock the secrets of your destiny through ancient Vedic wisdom. Expert in KP Astrology, 
            Medical Astrology, Vastu Shastra, and cosmic healing for health, wealth, and spiritual well-being.
          </p>
          <div className="flex gap-5 justify-center lg:justify-start mt-8 flex-wrap max-sm:flex-col max-sm:items-center">
            <a href="tel:+918084768303" className="btn-primary">
              <FaPhone /> Book Consultation
            </a>
            <Link to="/services" className="btn-secondary !border-white/40 !text-white hover:!bg-transparent hover:!border-accent-gold hover:!text-white">
              Explore Services <FaArrowRight />
            </Link>
          </div>
          <div className="flex items-center justify-center lg:justify-start gap-10 max-md:gap-5 mt-9 pt-6 border-t border-white/10 flex-shrink-0 max-md:flex-wrap">
            <div className="text-center">
              <span className="block font-heading text-[2.2rem] max-md:text-[1.6rem] font-bold text-accent-yellow">28+</span>
              <span className="text-[0.85rem] text-white/60 uppercase tracking-wider">Years Experience</span>
            </div>
            <div className="w-px h-10 bg-white/15 max-md:hidden" />
            <div className="text-center">
              <span className="block font-heading text-[2.2rem] max-md:text-[1.6rem] font-bold text-accent-yellow">10K+</span>
              <span className="text-[0.85rem] text-white/60 uppercase tracking-wider">Happy Clients</span>
            </div>
            <div className="w-px h-10 bg-white/15 max-md:hidden" />
            <div className="text-center">
              <span className="block font-heading text-[2.2rem] max-md:text-[1.6rem] font-bold text-accent-yellow">10+</span>
              <span className="text-[0.85rem] text-white/60 uppercase tracking-wider">Specializations</span>
            </div>
          </div>
            </div>

            {/* Hero Profile Image */}
            <div className="flex-shrink-0 order-1 lg:order-2 relative hero-image-wrapper">
              {/* Decorative glow ring */}
              <div className="absolute -inset-5 rounded-full border border-accent-gold/10 animate-spin-slow hidden lg:block" />
              <div className="absolute -inset-10 rounded-full border border-accent-gold/[0.05] hidden lg:block" style={{ animation: 'spin-slow 50s linear infinite reverse' }} />
              {/* Main image container */}
              <div className="relative w-[280px] h-[350px] lg:w-[420px] lg:h-[500px] rounded-[28px] overflow-hidden hero-profile-frame">
                <img src="/assets/profile-img-1.png" alt="Acharya Arun" className="w-full h-full object-cover object-top" />
                <div className="absolute inset-0 bg-gradient-to-t from-primary-deep/70 via-primary-deep/10 to-transparent" />
                <div className="absolute inset-0 bg-gradient-to-r from-primary-deep/20 via-transparent to-primary-deep/20" />
                {/* Name overlay at bottom */}
                <div className="absolute bottom-0 left-0 right-0 p-5 text-center">
                  <span className="font-heading text-lg text-accent-gold tracking-[2px] drop-shadow-[0_2px_8px_rgba(0,0,0,0.8)]">Acharya Arun</span>
                </div>
              </div>
              {/* Floating badge - bottom left */}
              <div className="absolute -bottom-3 -left-3 lg:-bottom-4 lg:-left-4 bg-white/10 backdrop-blur-xl text-white py-2.5 px-4 rounded-2xl border border-accent-gold/20 shadow-[0_8px_30px_rgba(0,0,0,0.3)] z-10 hidden md:block" style={{ animation: 'float 4s ease-in-out infinite 1s' }}>
                <span className="flex items-center gap-1.5 text-[0.75rem] font-semibold"><FaStar className="text-accent-saffron text-xs" /> 28+ Years Experience</span>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 z-[2] flex-shrink-0">
          <div className="w-[26px] h-10 border-2 border-accent-gold/50 rounded-[15px] flex justify-center pt-1.5">
            <div className="w-1 h-2 bg-accent-gold rounded animate-float" />
          </div>
        </div>
      </section>

      {/* ===== ZODIAC MARQUEE ===== */}
      <section className="zodiac-marquee relative py-[22px] overflow-hidden border-y border-accent-gold/15" style={{ background: 'linear-gradient(90deg, #151136, #1a1540, #151136)' }}>
        <div className="absolute top-0 bottom-0 left-0 w-[120px] z-[2] pointer-events-none" style={{ background: 'linear-gradient(90deg, #151136, transparent)' }} />
        <div className="absolute top-0 bottom-0 right-0 w-[120px] z-[2] pointer-events-none" style={{ background: 'linear-gradient(270deg, #151136, transparent)' }} />
        <div className="flex gap-[50px] animate-marquee-scroll w-max">
          {[...zodiacData, ...zodiacData].map((z, i) => (
            <div key={i} className="flex items-center gap-2.5 flex-shrink-0">
              <span className="text-[1.8rem] text-accent-saffron">{z.sign}</span>
              <span className="font-heading text-white text-[0.95rem] tracking-wider">{z.name}</span>
              <span className="text-white/40 text-[0.8rem]">{z.hindi}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ===== SERVICES PREVIEW ===== */}
      <section className="py-20 md:py-28 bg-warm-off-white">
        <div className="container-custom">
          <div className="section-title">
            <h2>Our Divine Services</h2>
            <p>Discover cosmic solutions for every aspect of your life through ancient Vedic wisdom</p>
            <div className="decorative-line"><span></span><div className="dot"></div><span></span></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-[30px]">
            {services.map((service, index) => (
              <div key={index} className="service-card group bg-white rounded-2xl p-10 max-md:p-7 text-center transition-all duration-400 border border-warm-light-gray relative overflow-hidden hover:-translate-y-2.5 hover:shadow-[0_20px_40px_rgba(12,10,32,0.12),0_0_0_1px_rgba(167,139,250,0.1)]">
                <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-5 text-[1.8rem] text-primary-medium transition-all duration-300 group-hover:text-white group-hover:animate-pulse-glow" style={{ background: 'linear-gradient(135deg, rgba(167,139,250,0.1), rgba(249,115,22,0.1))' }}>
                  {service.icon}
                </div>
                <h3 className="text-[1.3rem] mb-3 text-primary-dark">{service.title}</h3>
                <p className="text-sm text-warm-gray mb-5">{service.desc}</p>
                <Link to="/services" className="inline-flex items-center gap-1.5 text-primary-medium font-semibold text-sm transition-all duration-300 hover:text-accent-gold hover:gap-2.5">
                  Learn More <FaArrowRight />
                </Link>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/services" className="btn-primary">
              View All Services <FaArrowRight />
            </Link>
          </div>
        </div>
      </section>

      {/* ===== ANIMATED COUNTERS ===== */}
      <section className="py-20 bg-gradient-cosmic relative overflow-visible">
        <div className="container-custom">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-[30px]">
            <CounterStat icon={<FaUsers />} target={10000} suffix="+" label="Happy Clients" />
            <CounterStat icon={<FaAward />} target={28} suffix="+" label="Years Experience" />
            <CounterStat icon={<FaGlobeAsia />} target={50} suffix="+" label="Cities Served" />
            <CounterStat icon={<FaCalendarAlt />} target={15000} suffix="+" label="Consultations" />
          </div>
        </div>
      </section>

      {/* ===== WHY CHOOSE US ===== */}
      <section className="py-20 md:py-28 bg-gradient-cosmic relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          {[...Array(20)].map((_, i) => (
            <div key={i} className="absolute w-1 h-1 bg-accent-gold/30 rounded-full animate-float" style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 5}s`,
            }} />
          ))}
        </div>
        <div className="container-custom relative z-[2]">
          <div className="section-title">
            <h2 className="!text-accent-gold">Why Choose Acharya Arun?</h2>
            <p className="!text-white/70">Decades of experience in decoding the cosmic blueprint of your life</p>
            <div className="decorative-line"><span></span><div className="dot"></div><span></span></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-[30px]">
            {[
              { icon: <GiSpellBook />, title: 'Vedic Expertise', desc: 'Deep knowledge of ancient Vedic scriptures combined with KP astrology for accurate predictions.' },
              { icon: <GiMeditation />, title: 'Holistic Healing', desc: 'Unique combination of medical astrology and cosmic healing for non-treatable conditions.' },
              { icon: <FaHandsHelping />, title: 'Personalized Guidance', desc: 'Every consultation is deeply personalized with detailed chart analysis and practical remedies.' },
              { icon: <FaStar />, title: 'Proven Results', desc: 'Thousands of satisfied clients worldwide with life-changing transformations and accurate timing.' },
            ].map((item, i) => (
              <div key={i} className="group bg-white/[0.04] backdrop-blur-[16px] border border-accent-gold/10 rounded-[20px] py-11 px-6 text-center transition-all duration-400 relative overflow-hidden hover:bg-white/[0.08] hover:-translate-y-2 hover:border-accent-gold/35 hover:shadow-[0_20px_40px_rgba(0,0,0,0.3),0_0_30px_rgba(167,139,250,0.06)]">
                <div className="text-[2.5rem] text-accent-gold mb-5 drop-shadow-[0_0_10px_rgba(167,139,250,0.3)] transition-all duration-300 group-hover:scale-[1.15] group-hover:drop-shadow-[0_0_20px_rgba(167,139,250,0.6)]">{item.icon}</div>
                <h3 className="font-heading text-white text-[1.15rem] mb-3">{item.title}</h3>
                <p className="text-white/60 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CONSULTATION MODES ===== */}
      <section className="py-20 md:py-28 bg-white">
        <div className="container-custom">
          <div className="section-title">
            <h2>Consult Your Way</h2>
            <p>Multiple convenient ways to connect with Acharya Arun from anywhere in the world</p>
            <div className="decorative-line"><span></span><div className="dot"></div><span></span></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-[30px] items-stretch">
            <div className="group bg-warm-off-white rounded-[20px] pt-[50px] pb-10 px-[30px] text-center border-2 border-warm-light-gray transition-all duration-400 relative hover:-translate-y-2 hover:shadow-lg hover:border-accent-gold">
              <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-5 transition-all duration-300" style={{ background: 'linear-gradient(135deg, rgba(167,139,250,0.15), rgba(249,115,22,0.15))' }}>
                <FaPhoneAlt className="text-[1.8rem] text-accent-saffron" />
              </div>
              <h3 className="text-[1.3rem] mb-3 text-primary-dark">Phone Consultation</h3>
              <p className="text-sm text-warm-gray mb-6 leading-relaxed">Direct voice consultation for detailed chart analysis and guidance. Available 9 AM - 9 PM IST.</p>
              <a href="tel:+918084768303" className="inline-block py-2.5 px-[30px] rounded-full font-semibold text-sm transition-all duration-300 bg-gradient-gold text-primary-dark hover:scale-105 hover:shadow-gold-sm">Call Now</a>
            </div>
            <div className="group bg-gradient-cosmic rounded-[20px] pt-[50px] pb-10 px-[30px] text-center border-2 border-accent-gold transition-all duration-400 relative md:scale-105 hover:-translate-y-2 hover:shadow-lg max-md:scale-100">
              <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-gradient-gold text-primary-dark py-1.5 px-[22px] rounded-full text-xs font-bold uppercase tracking-wider whitespace-nowrap">Most Popular</div>
              <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-5 bg-white/10 border border-accent-gold/30">
                <FaVideo className="text-[1.8rem] text-accent-yellow" />
              </div>
              <h3 className="text-[1.3rem] mb-3 text-accent-gold">Video Consultation</h3>
              <p className="text-sm text-white/[0.85] mb-6 leading-relaxed">Face-to-face virtual sessions with screen sharing for detailed horoscope walkthrough.</p>
              <button onClick={openContactModal} className="inline-block py-2.5 px-[30px] rounded-full font-semibold text-sm transition-all duration-300 bg-gradient-gold text-primary-dark hover:scale-105 hover:shadow-gold-sm cursor-pointer border-none">Book Session</button>
            </div>
            <div className="group bg-warm-off-white rounded-[20px] pt-[50px] pb-10 px-[30px] text-center border-2 border-warm-light-gray transition-all duration-400 relative hover:-translate-y-2 hover:shadow-lg hover:border-accent-gold">
              <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-5 transition-all duration-300" style={{ background: 'linear-gradient(135deg, rgba(167,139,250,0.15), rgba(249,115,22,0.15))' }}>
                <FaComments className="text-[1.8rem] text-accent-saffron" />
              </div>
              <h3 className="text-[1.3rem] mb-3 text-primary-dark">WhatsApp Chat</h3>
              <p className="text-sm text-warm-gray mb-6 leading-relaxed">Quick queries and follow-up consultations via WhatsApp. Share birth details easily.</p>
              <a href="https://wa.me/919835040629" target="_blank" rel="noopener noreferrer" className="inline-block py-2.5 px-[30px] rounded-full font-semibold text-sm transition-all duration-300 bg-gradient-gold text-primary-dark hover:scale-105 hover:shadow-gold-sm">Chat Now</a>
            </div>
          </div>
        </div>
      </section>

      {/* ===== SPECIALIZATIONS ===== */}
      <section className="py-20 md:py-28 bg-white">
        <div className="container-custom">
          <div className="section-title">
            <h2>Areas of Expertise</h2>
            <p>Comprehensive astrology services covering every dimension of your life</p>
            <div className="decorative-line"><span></span><div className="dot"></div><span></span></div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: <FaSun />, name: 'Horoscope Making', desc: 'Detailed birth chart preparation' },
              { icon: <FaMoon />, name: 'Muhurta Astrology', desc: 'Auspicious timing for events' },
              { icon: <FaGem />, name: 'Gem Therapy', desc: 'Planetary gemstone remedies' },
              { icon: <FaHeart />, name: 'Match Making', desc: 'Marriage compatibility analysis' },
              { icon: <FaHome />, name: 'Vastu Expert', desc: 'Space energy harmonization' },
              { icon: <GiCrystalBall />, name: 'KP Astrology', desc: 'Precise predictive system' },
              { icon: <FaOm />, name: 'Graha Shanti', desc: 'Planetary peace remedies' },
              { icon: <GiMeditation />, name: 'Astro Healing', desc: 'Cosmic energy healing' },
            ].map((spec, i) => (
              <div key={i} className="group text-center py-9 px-5 rounded-2xl bg-warm-off-white border border-transparent transition-all duration-400 relative hover:border-accent-gold/20 hover:-translate-y-2 hover:shadow-[0_15px_35px_rgba(12,10,32,0.1)] hover:bg-white">
                <div className="text-[2rem] text-accent-saffron mb-4">{spec.icon}</div>
                <h4 className="text-base text-primary-dark mb-2">{spec.name}</h4>
                <p className="text-[0.8rem] text-warm-gray">{spec.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== DAILY HOROSCOPE TEASER ===== */}
      <section className="py-20 md:py-28 bg-gradient-cosmic relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          {[...Array(20)].map((_, i) => (
            <div key={i} className="absolute bg-white rounded-full animate-twinkle" style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`,
              width: `${Math.random() * 2 + 1}px`,
              height: `${Math.random() * 2 + 1}px`,
            }} />
          ))}
        </div>
        <div className="container-custom relative z-[2]">
          <div className="section-title">
            <h2 className="!text-accent-gold">Explore Your Zodiac</h2>
            <p className="!text-white/70">Select your sign for personalized cosmic insights</p>
            <div className="decorative-line"><span></span><div className="dot"></div><span></span></div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-5">
            {zodiacData.map((z, i) => (
              <div key={i} onClick={() => navigate(`/horoscope-2026#${z.name.toLowerCase()}`)} className="group flex flex-col items-center gap-2 py-7 px-4 bg-white/[0.04] border border-accent-gold/10 rounded-[20px] backdrop-blur-[16px] transition-all duration-400 no-underline cursor-pointer relative overflow-hidden hover:bg-white/10 hover:border-accent-gold/40 hover:-translate-y-2 hover:scale-[1.04] hover:shadow-[0_15px_30px_rgba(0,0,0,0.3),0_0_25px_rgba(167,139,250,0.08)]">
                <span className="text-[2.5rem] text-accent-yellow transition-transform duration-300 group-hover:scale-[1.2]">{z.sign}</span>
                <span className="font-heading text-white text-[0.85rem] tracking-wider">{z.name}</span>
                <span className="text-white/40 text-[0.75rem]">{z.hindi}</span>
              </div>
            ))}
          </div>

          <div className="text-center mt-10">
            <Link to="/horoscope-2026" className="btn-primary">
              Get Your Personalized Reading <FaArrowRight />
            </Link>
          </div>
        </div>
      </section>

      {/* ===== TESTIMONIALS ===== */}
      <section className="py-20 md:py-28 bg-warm-off-white">
        <div className="container-custom">
          <div className="section-title">
            <h2>Client Experiences</h2>
            <p>Hear from those whose lives have been transformed through cosmic guidance</p>
            <div className="decorative-line"><span></span><div className="dot"></div><span></span></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-[30px]">
            {testimonials.map((t, i) => (
              <div key={i} className="bg-white rounded-[20px] p-9 max-md:p-6 relative border border-warm-light-gray transition-all duration-400 border-l-4 hover:-translate-y-2 hover:shadow-[0_20px_40px_rgba(12,10,32,0.1)]" style={{ borderImage: 'linear-gradient(to bottom, #a78bfa, #f97316) 1', borderImageSlice: '1 0 1 4' }}>
                <div className="flex gap-[3px] mb-4">
                  {[...Array(t.rating)].map((_, j) => (
                    <FaStar key={j} className="text-accent-yellow text-sm" />
                  ))}
                </div>
                <FaQuoteLeft className="text-[2rem] text-accent-gold/25 mb-4" />
                <p className="text-[0.95rem] text-warm-text-body italic mb-5 leading-[1.7]">{t.text}</p>
                <div className="flex items-center gap-3">
                  <div className="w-[45px] h-[45px] rounded-full bg-gradient-warm flex items-center justify-center text-white font-bold text-lg">
                    {t.name.charAt(0)}
                  </div>
                  <div>
                    <h4 className="text-[0.95rem] text-primary-dark font-body">{t.name}</h4>
                    <span className="text-[0.8rem] text-warm-gray">{t.location}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== VEDIC QUOTE ===== */}
      <section className="py-20 bg-warm-off-white">
        <div className="container-custom">
          <div className="quote-box max-w-[700px] mx-auto text-center py-[60px] px-[50px] max-md:py-10 max-md:px-6 bg-white rounded-3xl border border-warm-light-gray shadow-[0_8px_40px_rgba(12,10,32,0.06)] relative overflow-hidden">
            <FaOm className="text-[2.5rem] text-accent-gold mb-5 animate-float" />
            <blockquote className="font-accent text-[1.6rem] max-md:text-[1.2rem] text-primary-dark mb-4 leading-relaxed italic">
              "ज्योतिषां सूर्यादिग्रहाणां बोधकं शास्त्रम्"
            </blockquote>
            <p className="text-base text-warm-gray italic mb-2.5">
              "Jyotish is the sacred science that reveals the wisdom of the Sun and celestial bodies"
            </p>
            <span className="text-[0.85rem] text-accent-gold font-semibold tracking-wider">— Vedic Scripture</span>
          </div>
        </div>
      </section>

      {/* ===== CTA SECTION ===== */}
      <section className="py-[120px] max-md:py-20 bg-gradient-cosmic relative overflow-hidden text-center">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(167,139,250,0.04), transparent 60%)' }} />
        <div className="absolute inset-0 pointer-events-none">
          {[...Array(15)].map((_, i) => (
            <div key={i} className="absolute w-[3px] h-[3px] bg-white rounded-full animate-twinkle" style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`,
            }} />
          ))}
        </div>
        <div className="container-custom relative z-[2]">
          <FaOm className="text-5xl text-accent-gold mb-5 animate-float mx-auto" />
          <h2 className="text-[2.5rem] max-md:text-[1.8rem] text-white mb-4 font-heading">Ready to Discover Your Cosmic Path?</h2>
          <p className="text-lg text-white/70 max-w-[600px] mx-auto mb-8">Connect with Acharya Arun for personalized astrological guidance and begin your journey of healing and transformation.</p>
          <div className="flex gap-5 justify-center flex-wrap">
            <a href="tel:+919999999999" className="btn-primary">
              <FaPhone /> Call Now
            </a>
            <button onClick={openContactModal} className="btn-secondary !border-white !text-white hover:!bg-transparent hover:!border-accent-gold hover:!text-white">
              Book Appointment
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
